document.addEventListener('DOMContentLoaded', () => {
  if (!window.gsap) return

  const { gsap } = window

  const section = document.querySelector('[data-home-hero-section]')
  if (!section) return

  const heroMedia = section.querySelector('[data-hero-media]')
  const heroOverlay = section.querySelector('[data-hero-overlay]')
  const heroControls = section.querySelectorAll('[data-hero-controls] > *')
  const scrollButton = section.querySelector('[data-hero-scroll]')

  const isDesignMode = Boolean(window.theme?.designMode)
  const isFormReturn = /^(#NewsletterForm-|#ContactForm$|#CancelOrderForm$)/.test(window.location.hash)
  const HERO_REVEAL_DURATION = 1.2
  const isDesktop = () => window.matchMedia('(min-width: 1200px)').matches

  let revealed = false
  let revealing = false
  let lockedScrollY = 0

  // Scroll lock
  const isScrollLocked = () => !revealed && !isDesignMode

  const lockScroll = () => {
    lockedScrollY = window.scrollY

    document.documentElement.setAttribute('data-hero-scroll-locked', '')

    document.body.style.position = 'fixed'
    document.body.style.top = `-${lockedScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    if (window.lenis) {
      window.lenis.stop()
      window.lenis.scrollTo(0, { immediate: true })
    }
  }

  const unlockScroll = (scrollTo = lockedScrollY) => {
    document.documentElement.removeAttribute('data-hero-scroll-locked')

    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.width = ''

    window.scrollTo(0, scrollTo)

    if (window.lenis) {
      window.lenis.scrollTo(scrollTo, { immediate: true })
      window.lenis.start()
    }
  }

  const scrollBlockKeys = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', ' ', 'Home', 'End'])

  const getCookiePreferencesView = () => {
    const prefsView = document.querySelector('[data-cookie-preferences-view]')
    if (!prefsView || prefsView.classList.contains('hidden')) return null
    return prefsView
  }

  const getOpenCartDrawer = () => {
    const drawer = document.getElementById('CartDrawer-Container')
    if (!drawer || drawer.classList.contains('invisible')) return null
    return drawer
  }

  const preventScroll = (event) => {
    if (!isScrollLocked()) return

    const cartDrawer = getOpenCartDrawer()
    if (cartDrawer?.contains(event.target)) return

    if (event.target.closest?.('[data-region-list]')) return

    const prefsView = getCookiePreferencesView()
    if (prefsView?.contains(event.target)) {
      const scrollEl = prefsView.querySelector('[data-cookie-preferences-scroll]')

      if (event.type === 'touchmove' && scrollEl?.contains(event.target)) return

      if (event.type === 'wheel' && scrollEl) {
        if (scrollEl.contains(event.target)) return

        scrollEl.scrollTop += event.deltaY
      }

      event.preventDefault()
      return
    }

    event.preventDefault()
  }

  const preventScrollKeys = (event) => {
    if (!isScrollLocked()) return

    const cartDrawer = getOpenCartDrawer()
    if (cartDrawer?.contains(event.target)) return

    const prefsView = getCookiePreferencesView()
    if (prefsView?.contains(event.target)) return

    if (scrollBlockKeys.has(event.key)) event.preventDefault()
  }

  const clampScroll = () => {
    if (!isScrollLocked()) return

    if (window.scrollY !== 0) {
      window.scrollTo(0, 0)
    }

    if (window.lenis && window.lenis.scroll !== 0) {
      window.lenis.scrollTo(0, { immediate: true })
    }
  }

  const addScrollBlockers = () => {
    document.addEventListener('wheel', preventScroll, { passive: false, capture: true })
    document.addEventListener('touchmove', preventScroll, { passive: false, capture: true })
    document.addEventListener('keydown', preventScrollKeys, { capture: true })
    window.addEventListener('scroll', clampScroll, { passive: true, capture: true })

    if (window.lenis) {
      window.lenis.on('scroll', clampScroll)
    }
  }

  const removeScrollBlockers = () => {
    document.removeEventListener('wheel', preventScroll, { capture: true })
    document.removeEventListener('touchmove', preventScroll, { capture: true })
    document.removeEventListener('keydown', preventScrollKeys, { capture: true })
    window.removeEventListener('scroll', clampScroll, { capture: true })

    if (window.lenis) {
      window.lenis.off('scroll', clampScroll)
    }
  }

  // Hero reveal
  const resetHeroToDefault = () => {
    gsap.set(section, { clearProps: 'transform,position,top,left,width,zIndex,pointerEvents' })

    const offset = section.offsetHeight

    if (window.ScrollTrigger) {
      window.ScrollTrigger.refresh()
    }

    return offset
  }

  const syncHeaderBlend = () => {
    document.dispatchEvent(new CustomEvent('header:update-blend'))
  }

  // Show header once heritage starts scrolling away
  const showHeaderOnHeritageScroll = () => {
    const heritage = document.querySelector('[data-heritage-section]')
    if (!heritage) {
      document.dispatchEvent(new CustomEvent('header:show'))
      return
    }

    const onScroll = () => {
      if (heritage.getBoundingClientRect().top > -40) return

      window.removeEventListener('scroll', onScroll)
      if (window.lenis) window.lenis.off('scroll', onScroll)
      document.dispatchEvent(new CustomEvent('header:show'))
    }

    requestAnimationFrame(() => {
      window.addEventListener('scroll', onScroll, { passive: true })
      if (window.lenis) window.lenis.on('scroll', onScroll)
    })
  }

  const finishReveal = () => {
    revealing = false
    removeScrollBlockers()

    const offset = resetHeroToDefault()
    unlockScroll(offset)
    syncHeaderBlend()
    revealed = true
    section.setAttribute('data-hero-revealed', '')
    if (!isDesktop()) showHeaderOnHeritageScroll()

    heroMedia?.removeAttribute('role')
    heroMedia?.removeAttribute('tabindex')
    heroMedia?.classList.remove('cursor-pointer')
  }

  const reveal = () => {
    if (revealed || revealing || isDesignMode) return
    revealing = true

    if (!isDesktop()) {
      document.dispatchEvent(
        new CustomEvent('header:hide', { detail: { duration: HERO_REVEAL_DURATION } }),
      )
    }

    gsap.to(section, {
      y: '-100%',
      duration: HERO_REVEAL_DURATION,
      ease: 'power3.inOut',
      onUpdate: syncHeaderBlend,
      onComplete: finishReveal,
    })
  }

  // On load animation
  const buildEntrance = (tl) => {
    if (heroMedia) {
      gsap.set(heroMedia, { scale: 1.06, filter: 'brightness(1.4)' })
      tl.to(heroMedia, { scale: 1, filter: 'brightness(1)', duration: 1.6, ease: 'power3.out', clearProps: 'filter' }, 0)
    }

    if (heroOverlay) {
      gsap.set(heroOverlay, { opacity: 1 })
      tl.to(heroOverlay, { opacity: 0, duration: 1.1, ease: 'power2.out' }, 0)
    }

    if (heroControls.length) {
      gsap.set(heroControls, { autoAlpha: 0, transition: 'none' })
      heroControls.forEach((el) => el.classList.remove('opacity-0'))
      tl.to(heroControls, { autoAlpha: 1, duration: 0.5, ease: 'power2.out', clearProps: 'opacity,visibility,transition' }, 0.2)
    }
  }

  // Init
  if (!isDesignMode && !isFormReturn) {
    gsap.set(section, { position: 'fixed', left: 0, width: '100%', zIndex: 40 })
    section.style.top = 'var(--announcement-height)'
    window.scrollTo(0, 0)
    lockScroll()
    addScrollBlockers()
  } else if (isFormReturn) {
    revealed = true
    section.setAttribute('data-hero-revealed', '')
    heroMedia?.removeAttribute('role')
    heroMedia?.removeAttribute('tabindex')
    heroMedia?.classList.remove('cursor-pointer')
    document.dispatchEvent(new CustomEvent('header:show'))
  }

  buildEntrance(gsap.timeline({ delay: 0.1 }))

  // Reveal triggers
  const scrollToNextSection = () => {
    const wrapper = section.closest('[id^="shopify-section"]') || section
    const next = wrapper.nextElementSibling
    const targetY = next ? next.getBoundingClientRect().top + window.scrollY : wrapper.getBoundingClientRect().bottom + window.scrollY

    const distance = Math.abs(targetY - window.scrollY)
    if (distance < 1) return

    const duration = (distance / window.innerHeight) * HERO_REVEAL_DURATION

    if (window.lenis) {
      window.lenis.scrollTo(targetY, { duration, easing: gsap.parseEase('power3.inOut') })
      return
    }

    const scroll = { y: window.scrollY }
    gsap.to(scroll, {
      y: targetY,
      duration,
      ease: 'power3.inOut',
      onUpdate: () => window.scrollTo(0, scroll.y),
    })
  }

  scrollButton?.addEventListener('click', () => {
    if (revealed) scrollToNextSection()
    else reveal()
  })

  // Scroll arrow intro
  const arrowTrack = scrollButton?.querySelector('.arrow-track')

  if (scrollButton && arrowTrack) {
    const endArrowIntro = () => scrollButton.setAttribute('data-arrow-intro-done', '')

    arrowTrack.addEventListener('animationend', endArrowIntro, { once: true })
    scrollButton.addEventListener('pointerenter', endArrowIntro, { once: true })
  }

  heroMedia?.addEventListener('click', () => {
    if (!revealed) reveal()
  })

  heroMedia?.addEventListener('keydown', (event) => {
    if (revealed) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      reveal()
    }
  })

  window.initVideoSoundToggle?.(section, '[data-hero-video]', { stopPropagation: true })
})
